import { type FunctionComponent } from 'react';
import { type RatingFieldsFragment } from '../../../__generated__/graphql-client-types';
import { buildGTMCollectionLinkClick } from '../../../helpers/analytics/gtm/event-builders';
import { StyledButton } from '../../buttons';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { Rating } from '../rating/rating.component';

export type ProductSummaryProps = {
	brand: string;
	collectionName?: string;
	discontinuedOnClick?: () => void;
	isDiscontinued?: boolean;
	modelNumber: string;
	productUid: number;
	title: string;
	// This clamps just the title section(not the brand) on mobile.
	shouldClampTitle?: boolean;
	reviewSummary?: RatingFieldsFragment;
	url?: string;
	selectedVariant?: number;
};

export const ProductSummary: FunctionComponent<ProductSummaryProps> = ({
	brand,
	collectionName,
	discontinuedOnClick,
	isDiscontinued = false,
	modelNumber,
	productUid,
	title,
	shouldClampTitle = false,
	reviewSummary,
	url,
	selectedVariant
}) => {
	return (
		<section className="flex flex-column theme-grey-darker pv0 pt2-ns" data-testid="product-summary">
			<h1 className={`ma0 fw6 lh-title di f5 f3-ns`} data-testid="product-summary-h1">
				<span>{brand}</span> <span className={`fw2 di-ns ${shouldClampTitle ? 'tc3-title' : ''}`}>{title}</span>
			</h1>
			<h3 className="f7 fw4 lh-title theme-grey mt2 mt1-ns mb0 flex flex-row">
				<div>
					Model:<span data-automation="product-model-number"> {modelNumber}</span>
				</div>
				<div className="ph1">|</div>
				<div>
					Item:<span data-automation="product-item-number"> bci{productUid}</span>
				</div>
			</h3>
			{collectionName && (
				<div data-testid="collection" className="pt1-ns dib f6 f5-ns ma0 theme-grey-darker">
					from the
					<StyledLink
						url={`/collection/${encodeURIComponent(brand)}/${encodeURIComponent(collectionName)}`}
						className="pl1"
						automationHook="top-collection"
						analyticsEvent={{ data: buildGTMCollectionLinkClick(collectionName, modelNumber) }}>
						{collectionName} Collection
					</StyledLink>
				</div>
			)}
			{isDiscontinued && discontinuedOnClick && (
				<StyledButton buttonStyle={'PRIMARY'} onClick={discontinuedOnClick}>
					Shop {brand}
				</StyledButton>
			)}
			{reviewSummary && (
				<div className="pt1">
					<Rating
						className={`f6`}
						ratingValue={reviewSummary.ratingValue}
						reviewCount={reviewSummary.reviewCount}
						url={url ? `${url}?uid=${selectedVariant}#reviews` : undefined}
					/>
				</div>
			)}
		</section>
	);
};
